import {AppAction} from '../appActions';
import {dataReducer} from '../data/dataReducer';
import {OptimisticUpdateActionsEnum} from './optimisticUpdatesActions';
import {
  hasOptimisticUpdatesFinishIdsActionExtension,
  optimisticUpdatesFinishIdsKey,
} from './optimisticUpdatesHelpers';
import {DataWithOptimisticUpdatesState, OptimisticUpdate} from './optimisticUpdatesState';

/*
 * Reducer.
 */

/** Applies the action to the original data state and re-applies optimistic updates. */
export function dataWithOptimisticUpdatesReducer(
  state: Partial<DataWithOptimisticUpdatesState> | undefined,
  action: AppAction,
): DataWithOptimisticUpdatesState {
  // The initial state in a Redux store can be undefined, even though createAppStore() prevents this in practice.
  if (!state) {
    throw new Error('Expected store to be preloaded with data');
  }

  // The initial state only has a data property, so use that to initialize the originalData property, if necessary.
  const prevOriginalData = state.originalData || state.data;
  if (!prevOriginalData) {
    throw new Error('Expected store to be preloaded with data');
  }

  // Reduce the base data.
  const originalData = dataReducer(prevOriginalData, action);

  // Reduce the optimistic updates themselves.
  const prevOptimisticUpdates = state.optimisticUpdates;
  const optimisticUpdates = optimisticUpdatesReducer(prevOptimisticUpdates, action);

  // Apply the optimistic updates to the raw data state.
  // Note: optimistic updates use memoization, and technically this introduces side-effects in our reducer.
  // This is justified, because it is a critical performance optimization, and it should not break time travel
  // and such.
  const data =
    !state.data || originalData !== prevOriginalData || optimisticUpdates !== prevOptimisticUpdates
      ? optimisticUpdates.reduce(
          (prevData, optimisticUpdate) => optimisticUpdate.updater(prevData),
          originalData,
        )
      : state.data;

  return {
    originalData,
    optimisticUpdates,
    data,
  };
}

function optimisticUpdatesReducer(state: OptimisticUpdate[] = [], action: AppAction): OptimisticUpdate[] {
  if (action.type === OptimisticUpdateActionsEnum.START_OPTIMISTIC_UPDATE) {
    const newOptimisticUpdate = action.payload;

    // If this optimistic update already exists, replace it at the same place in the sequence.
    const existingIndex = state.findIndex(
      (optimisticUpdate) => optimisticUpdate.id === newOptimisticUpdate.id,
    );
    if (existingIndex !== -1) {
      const newState = [...state];
      newState[existingIndex] = newOptimisticUpdate;
      return newState;
    }

    return [...state, newOptimisticUpdate];
  }

  if (hasOptimisticUpdatesFinishIdsActionExtension(action)) {
    const finishIds = new Set(action[optimisticUpdatesFinishIdsKey]);
    const newState = state.filter((optimisticUpdate) => !finishIds.has(optimisticUpdate.id));

    // Don't do anything if no optimistic updates were removed.
    if (state.length === newState.length) {
      return state;
    }

    return newState;
  }

  return state;
}
